"use client";

import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Button } from "@/app/components/ui/button";
import { Textarea } from "@/app/components/ui/textarea";
import { Input } from "@/app/components/ui/input";
import { Label } from "@/app/components/ui/label";
import { Alert, AlertDescription } from "@/app/components/ui/alert";
import { Copy, RotateCcw, CheckCircle, XCircle, AlertTriangle, Search, Zap } from "lucide-react";
import { toast } from "react-hot-toast";

export default function RegexTesterTool() {
  const [pattern, setPattern] = useState("");
  const [testText, setTestText] = useState("");
  const [flags, setFlags] = useState("g");
  const [matches, setMatches] = useState<string[]>([]);
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [errorMessage, setErrorMessage] = useState("");

  const testRegex = () => {
    if (!pattern.trim()) {
      setErrorMessage("Please enter a regex pattern");
      setIsValid(false);
      return;
    }

    try {
      const regex = new RegExp(pattern, flags);
      const matchResults = testText.match(regex);
      setMatches(matchResults || []);
      setIsValid(true);
      setErrorMessage("");
    } catch (error) {
      setIsValid(false);
      setErrorMessage("Invalid regex pattern");
      setMatches([]);
    }
  };

  const replaceText = () => {
    if (!pattern.trim()) {
      setErrorMessage("Please enter a regex pattern");
      setIsValid(false);
      return;
    }

    try {
      const regex = new RegExp(pattern, flags);
      const replacement = prompt("Enter replacement text:");
      if (replacement !== null) {
        const result = testText.replace(regex, replacement);
        setTestText(result);
        toast.success("Text replaced!");
      }
    } catch (error) {
      setIsValid(false);
      setErrorMessage("Invalid regex pattern");
    }
  };

  const copyMatches = () => {
    if (matches.length === 0) return;
    navigator.clipboard.writeText(matches.join("\n"))
      .then(() => toast.success("Matches copied to clipboard!"))
      .catch(() => toast.error("Failed to copy matches."));
  };

  const clearAll = () => {
    setPattern("");
    setTestText("");
    setMatches([]);
    setIsValid(null);
    setErrorMessage("");
  };

  const loadSample = (type: string) => {
    const samples = {
      email: {
        pattern: "\\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Z|a-z]{2,}\\b",
        text: "Contact us at john@example.com or jane.doe@company.org for more information.",
        flags: "g"
      },
      phone: {
        pattern: "\\b\\d{3}[-.]?\\d{3}[-.]?\\d{4}\\b",
        text: "Call us at 555-123-4567 or 555.987.6543 for support.",
        flags: "g"
      },
      url: {
        pattern: "https?://[\\w\\-]+(\\.[\\w\\-]+)+([\\w\\-\\.,@?^=%&:/~\\+#]*[\\w\\-\\@?^=%&/~\\+#])?",
        text: "Visit https://example.com or http://test.org for more info.",
        flags: "g"
      },
      word: {
        pattern: "\\b\\w{5,}\\b",
        text: "This is a sample text with various words of different lengths.",
        flags: "g"
      }
    };

    const sample = samples[type as keyof typeof samples];
    if (sample) {
      setPattern(sample.pattern);
      setTestText(sample.text);
      setFlags(sample.flags);
    }
  };

  const getHighlightedText = () => {
    if (!pattern.trim() || !testText.trim()) return testText;

    try {
      const regex = new RegExp(pattern, flags);
      return testText.replace(regex, (match) => `<mark class="bg-yellow-200 dark:bg-yellow-800 px-1 rounded">${match}</mark>`);
    } catch {
      return testText;
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="card-enhanced">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              Regex Pattern
              {isValid === true && <CheckCircle className="h-5 w-5 text-green-500" />}
              {isValid === false && <XCircle className="h-5 w-5 text-red-500" />}
            </CardTitle>
            <CardDescription>
              Enter your regular expression pattern
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pattern">Pattern</Label>
              <Input
                id="pattern"
                placeholder="/your-regex-pattern/"
                value={pattern}
                onChange={(e) => setPattern(e.target.value)}
                className="font-mono"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="flags">Flags</Label>
              <div className="flex gap-2">
                {['g', 'i', 'm', 's', 'u', 'y'].map(flag => (
                  <Button
                    key={flag}
                    onClick={() => {
                      setFlags(flags.includes(flag) 
                        ? flags.replace(flag, '') 
                        : flags + flag
                      );
                    }}
                    variant={flags.includes(flag) ? "default" : "outline"}
                    size="sm"
                    className="hover-lift"
                  >
                    {flag}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                g: global, i: ignore case, m: multiline, s: dotall, u: unicode, y: sticky
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button onClick={testRegex} className="btn-enhanced hover-lift">
                <Zap className="h-4 w-4 mr-2" />
                Test Regex
              </Button>
              <Button onClick={replaceText} variant="outline" className="hover-lift">
                Replace
              </Button>
              <Button onClick={clearAll} variant="outline" className="hover-lift">
                <RotateCcw className="h-4 w-4 mr-2" />
                Clear
              </Button>
            </div>

            {errorMessage && (
              <Alert className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800 dark:text-red-200">
                  {errorMessage}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        <Card className="card-enhanced">
          <CardHeader>
            <CardTitle>Test Text</CardTitle>
            <CardDescription>
              Enter text to test your regex pattern against
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              placeholder="Enter text to test your regex pattern..."
              value={testText}
              onChange={(e) => setTestText(e.target.value)}
              className="min-h-[200px] font-mono text-sm"
            />
            
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => loadSample("email")} variant="outline" size="sm" className="hover-lift">
                Email
              </Button>
              <Button onClick={() => loadSample("phone")} variant="outline" size="sm" className="hover-lift">
                Phone
              </Button>
              <Button onClick={() => loadSample("url")} variant="outline" size="sm" className="hover-lift">
                URL
              </Button>
              <Button onClick={() => loadSample("word")} variant="outline" size="sm" className="hover-lift">
                Words
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="card-enhanced">
          <CardHeader>
            <CardTitle>Matches Found</CardTitle>
            <CardDescription>
              {matches.length} match{matches.length !== 1 ? 'es' : ''} found
            </CardDescription>
          </CardHeader>
          <CardContent>
            {matches.length > 0 ? (
              <div className="space-y-2">
                {matches.map((match, index) => (
                  <div key={index} className="p-2 bg-muted/50 rounded font-mono text-sm">
                    {match}
                  </div>
                ))}
                <Button onClick={copyMatches} variant="outline" className="hover-lift">
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Matches
                </Button>
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">
                No matches found. Try adjusting your pattern or test text.
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="card-enhanced">
          <CardHeader>
            <CardTitle>Highlighted Text</CardTitle>
            <CardDescription>
              Text with matches highlighted
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div 
              className="min-h-[200px] p-4 border rounded-lg bg-muted/20 font-mono text-sm"
              dangerouslySetInnerHTML={{ 
                __html: getHighlightedText() || "<p class='text-muted-foreground'>Enter pattern and text to see highlights</p>" 
              }}
            />
          </CardContent>
        </Card>
      </div>

      <Card className="card-enhanced">
        <CardHeader>
          <CardTitle>Common Regex Patterns</CardTitle>
          <CardDescription>
            Useful regex patterns for common use cases
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <h4 className="font-semibold">Text Patterns</h4>
              <div className="space-y-2 text-sm font-mono">
                <div><code>\b\w+@\w+\.\w+\b</code> - Email addresses</div>
                <div><code>\b\d{3}-\d{3}-\d{4}\b</code> - Phone numbers</div>
                <div><code>https?://\S+</code> - URLs</div>
                <div><code>\b[A-Z][a-z]+\b</code> - Capitalized words</div>
              </div>
            </div>
            <div className="space-y-3">
              <h4 className="font-semibold">Character Classes</h4>
              <div className="space-y-2 text-sm font-mono">
                <div><code>\d</code> - Any digit (0-9)</div>
                <div><code>\w</code> - Word character (a-z, A-Z, 0-9, _)</div>
                <div><code>\s</code> - Whitespace character</div>
                <div><code>.</code> - Any character except newline</div>
              </div>
            </div>
            <div className="space-y-3">
              <h4 className="font-semibold">Quantifiers</h4>
              <div className="space-y-2 text-sm font-mono">
                <div><code>*</code> - Zero or more</div>
                <div><code>+</code> - One or more</div>
                <div><code>?</code> - Zero or one</div>
                <div><code>{'{n,m}'}</code> - Between n and m times</div>
              </div>
            </div>
            <div className="space-y-3">
              <h4 className="font-semibold">Anchors</h4>
              <div className="space-y-2 text-sm font-mono">
                <div><code>^</code> - Start of string</div>
                <div><code>$</code> - End of string</div>
                <div><code>\b</code> - Word boundary</div>
                <div><code>\B</code> - Non-word boundary</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
